export const API_URL = process.env.REACT_APP_API_URL || "https://app.visaro.ng/api/v2";
export const API_URL_V3 = process.env.REACT_APP_API_URL_V3 || "https://app.visaro.ng/api/v3";
export const API_URL_V1 = process.env.REACT_APP_API_URL_V1 || "https://app.visaro.ng/api/v1";

// export const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/api/v2";
// export const API_URL_V3 = process.env.REACT_APP_API_URL_V3 || "http://127.0.0.1:8000/api/v3";
// export const API_URL_V1 = process.env.REACT_APP_API_URL_V1 || "http://127.0.0.1:8000/api/v1";
export const YV_PUBLIC_KEY = process.env.REACT_APP_YV_PUBLIC_KEY || "66e2b6da94ca4a5b7f112aee";

export type APIResponse<T = any> = {
  code?: string;
  message?: string;
  Message?: any;
  status?: boolean;
  success?: boolean;
  IsSuccessful?: boolean;
  errors?: Record<string, any>;
  data?: T;
  [key: string]: any;
};

export type APIErrorOptions = {
  /** HTTP status of the failed response, when the server actually answered. */
  status?: number;
  /** Parsed error body, so callers can read structured details the message drops. */
  responseData?: APIResponse;
  /** True when the request never produced a response, so the outcome is unknown. */
  isNetworkError?: boolean;
  /**
   * True when the server answered but we could not read the body. The request
   * was processed — we just cannot tell with what result — so this is as unknown
   * an outcome as a lost connection.
   */
  isUnreadableResponse?: boolean;
};

export class APIError extends Error {
  public validationErrors: Record<string, string[]>;
  public status?: number;
  public responseData?: APIResponse;
  public isNetworkError: boolean;
  public isUnreadableResponse: boolean;

  constructor(
    message: string,
    validationErrors: Record<string, string[]> = {},
    options: APIErrorOptions = {}
  ) {
    super(message);
    this.name = "APIError";
    this.validationErrors = validationErrors;
    this.status = options.status;
    this.responseData = options.responseData;
    this.isNetworkError = options.isNetworkError ?? false;
    this.isUnreadableResponse = options.isUnreadableResponse ?? false;
    Object.setPrototypeOf(this, APIError.prototype);
  }
}

function tryParseJsonMessage(s: string): string | null {
  const t = s.trim();
  if (!t.startsWith("{")) return null;
  try {
    const p = JSON.parse(t) as { error?: string; message?: string };
    return p.error ?? p.message ?? null;
  } catch {
    return null;
  }
}

/** Turn Laravel / gateway / raw JSON error bodies into a single user-facing string. */
export function normalizeApiErrorMessage(errorData: unknown): string {
  if (errorData == null) return "An error occurred";
  if (typeof errorData === "string") {
    const inner = tryParseJsonMessage(errorData);
    return inner ?? errorData;
  }
  if (typeof errorData !== "object") return String(errorData);
  const o = errorData as Record<string, unknown>;
  if (typeof o.message === "string" && o.message.trim()) {
    const inner = tryParseJsonMessage(o.message);
    if (inner) return inner;
    return o.message;
  }
  if (typeof o.Message === "string" && o.Message.trim()) return o.Message;
  const err = o.error;
  if (typeof err === "string") return err;
  if (err && typeof err === "object" && typeof (err as { message?: string }).message === "string") {
    return (err as { message: string }).message;
  }
  return "An error occurred";
}

async function parseErrorBody(response: Response): Promise<unknown> {
  const ct = response.headers.get("Content-Type") || "";
  if (ct.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return { message: await response.text() };
    }
  }
  const text = await response.text();
  return text ? { message: text } : {};
}

export const get = async (
  uri: string,
  options?: Omit<RequestInit, "method" | "url"> & {
    byPassStatus?: boolean;
  }
) => {
  const response = await fetch(uri, {
    method: "GET",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await parseErrorBody(response);
    throw new Error(normalizeApiErrorMessage(errorData));
  }

  const data = (await response.json()) as APIResponse;

  if (options?.byPassStatus) {
    return data;
  }

  if (!data.status && !data.success && data.IsSuccessful !== true) {
    const errorMsg = normalizeApiErrorMessage(data);
    if (data.errors) {
      // Throw a custom error object with both the message and validation errors
      throw new APIError(errorMsg, data.errors);
    } else {
      throw new Error(errorMsg);
    }
  }

  return data;
};

export const post = async (
  uri: string,
  body?: Record<any, any>,
  options?: Omit<RequestInit, "url">
) => {
  let response: Response;

  try {
    response = await fetch(uri, {
      method: "POST",
      body: JSON.stringify(body || {}),
      ...options,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(options?.headers || {}),
      },
    });
  } catch (error) {
    // fetch only rejects when no response came back at all. The request may still
    // have reached the server, so callers must not treat this as "nothing happened".
    throw new APIError(
      error instanceof Error ? error.message : "Network request failed",
      {},
      { isNetworkError: true }
    );
  }

  if (!response.ok) {
    const errorData = (await parseErrorBody(response)) as APIResponse;
    // Keep the parsed body + status on the error: some endpoints answer non-2xx
    // with structured details (error codes, references) the message alone loses.
    throw new APIError(
      normalizeApiErrorMessage(errorData),
      errorData?.errors || {},
      { status: response.status, responseData: errorData }
    );
  }

  let data: APIResponse;

  try {
    data = (await response.json()) as APIResponse;
  } catch (error) {
    // The server answered 2xx but we cannot read what it said. For a payment
    // that is not a failure — the charge may well have gone through — so flag it
    // as unknown rather than letting a raw SyntaxError look like a plain error.
    throw new APIError(
      "We could not read the server's response, so we cannot confirm the result.",
      {},
      { status: response.status, isUnreadableResponse: true }
    );
  }

  if (!data.status && !data.success && data.IsSuccessful !== true) {
    const errorMsg = normalizeApiErrorMessage(data);
    // Some endpoints answer HTTP 200 with a failure envelope. Carry the status
    // and parsed body here too, so structured details (error codes, references)
    // survive on the error exactly as they do on the non-2xx path above.
    throw new APIError(errorMsg, data.errors || {}, {
      status: response.status,
      responseData: data,
    });
  }

  return data;
};

export const postFormData = async (
  uri: string,
  body?: FormData,
  options?: Omit<RequestInit, "method" | "url">
) => {
  const response = await fetch(uri, {
    method: "POST",
    body: body,
    ...options,
    headers: {
      Accept: "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await parseErrorBody(response);
    throw new Error(normalizeApiErrorMessage(errorData));
  }

  const data = (await response.json()) as APIResponse;

  if (!data.status && !data.success && data.IsSuccessful !== true) {
    const errorMsg = normalizeApiErrorMessage(data);
    if (data.errors) {
      // Throw a custom error object with both the message and validation errors
      throw new APIError(errorMsg, data.errors);
    } else {
      throw new Error(errorMsg);
    }
  }

  return data;
};

export const patch = async (
  uri: string,
  body?: Record<any, any>,
  options?: Omit<RequestInit, "method" | "url">
) => {
  const response = await fetch(uri, {
    method: "PATCH",
    body: JSON.stringify(body || {}),
    ...options,
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await parseErrorBody(response);
    throw new Error(normalizeApiErrorMessage(errorData));
  }

  const data = (await response.json()) as APIResponse;

  if (!data.status && !data.success && data.IsSuccessful !== true) {
    const errorMsg = normalizeApiErrorMessage(data);
    if (data.errors) {
      throw new APIError(errorMsg, data.errors);
    } else {
      throw new Error(errorMsg);
    }
  }

  return data;
};

export const del = async (
  uri: string,
  options?: Omit<RequestInit, "method" | "url">
) => {
  const response = await fetch(uri, {
    method: "DELETE",
    ...options,
    headers: {
      Accept: "application/json",
      ...(options?.headers || {}),
    },
  });

  if (!response.ok) {
    const errorData = await parseErrorBody(response);
    throw new Error(normalizeApiErrorMessage(errorData));
  }

  const data = (await response.json()) as APIResponse;

  if (!data.status && !data.success && data.IsSuccessful !== true) {
    const errorMsg = normalizeApiErrorMessage(data);
    if (data.errors) {
      throw new APIError(errorMsg, data.errors);
    } else {
      throw new Error(errorMsg);
    }
  }

  return data;
};
